import { useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";

export interface SegmentOption<T> {
  value: T;
  label: ReactNode;
}

/**
 * A row of segments: tabs (`tablist`) or a single choice (`radiogroup`). On a narrow screen the row scrolls sideways without a visible
 * bar, a fade at the edge shows that there is more, and the chosen segment is scrolled into view. The arrow keys, Home and End change
 * the choice. A swipe on the row scrolls it and never moves to another character.
 */
export function Segments<T>(props: {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
  role?: "tablist" | "radiogroup";
}) {
  const role = props.role ?? "radiogroup";
  const scroller = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ start: false, end: false });
  const selectedIndex = props.options.findIndex((o) => Object.is(o.value, props.value));

  const measure = useCallback(() => {
    const element = scroller.current;
    if (!element) return;
    const start = element.scrollLeft > 1;
    const end = element.scrollLeft + element.clientWidth < element.scrollWidth - 1;
    setEdges((e) => (e.start === start && e.end === end ? e : { start, end }));
  }, []);

  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    measure();
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(measure) : null;
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [measure]);

  // The chosen segment comes into view when the row opens and whenever the choice changes; only the row scrolls, not the page.
  useLayoutEffect(() => {
    const element = scroller.current;
    const button = element?.querySelectorAll<HTMLElement>("[data-segment]")[selectedIndex];
    if (!element || !button) return;
    const left = button.offsetLeft;
    const margin = 24;
    if (left - margin < element.scrollLeft) element.scrollLeft = Math.max(0, left - margin);
    else if (left + button.offsetWidth + margin > element.scrollLeft + element.clientWidth) element.scrollLeft = left + button.offsetWidth + margin - element.clientWidth;
    measure();
  }, [selectedIndex, measure]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const last = props.options.length - 1;
    // Without a choice the first arrow step lands on the first or last segment.
    const at = selectedIndex;
    const next =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? at < 0 ? 0 : (at + 1) % (last + 1)
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? at < 0 ? last : (at - 1 + last + 1) % (last + 1)
          : e.key === "Home"
            ? 0
            : e.key === "End"
              ? last
              : null;
    if (next === null) return;
    e.preventDefault();
    const option = props.options[next];
    if (!option) return;
    // A segment already chosen stays chosen; callers that toggle on a second click must not lose it by Home or End.
    if (next !== selectedIndex) props.onChange(option.value);
    scroller.current?.querySelectorAll<HTMLElement>("[data-segment]")[next]?.focus();
  };

  return (
    <div
      ref={scroller}
      className={`segments-scroll${edges.start ? " more-start" : ""}${edges.end ? " more-end" : ""}`}
      data-own-swipe=""
      onScroll={measure}
    >
      <div className="segments" role={role} aria-label={props.ariaLabel} onKeyDown={onKeyDown}>
        {props.options.map((o, i) => {
          const selected = i === selectedIndex;
          return (
            <button
              key={i}
              type="button"
              data-segment=""
              role={role === "tablist" ? "tab" : "radio"}
              aria-selected={role === "tablist" ? selected : undefined}
              aria-checked={role === "radiogroup" ? selected : undefined}
              tabIndex={selected || (selectedIndex < 0 && i === 0) ? 0 : -1}
              className={selected ? "selected" : ""}
              onClick={() => props.onChange(o.value)}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
